import { createElement, type CSSProperties, type ReactElement, type ReactNode } from "react";
import {
    color,
    typography,
    typographyRoles,
    type TypographyScaleKey,
    type TypographyWeightKey,
    type TypographyOpticalKey,
    type TypographyCapsKey,
    type TypographyRoleKey,
    type TypographyLineHeightKey,
} from "@tokens/index";

/**
 * Text primitive.
 *
 * Two-mode API:
 *
 *   1. **Role-based (preferred).** Pass `role="eyebrow" | "label" | "title"
 *      | "body" | …` to apply the project's semantic type ramp. The role
 *      bundles size, weight, leading, tracking, transform, and (where
 *      relevant) family + tabular numerals. Aesthetic-theory §3 (Bringhurst:
 *      "name your styles by *role*, not by pixel size") and §7 (hierarchy
 *      methods).
 *
 *   2. **Explicit (escape hatch).** Pass `size` / `weight` / `leading` /
 *      `tracking` / `transform` directly when the role catalog can't
 *      express what you need (rare). Explicit props *override* the role's
 *      bundle, allowing a one-off heading that's a hair smaller without
 *      forking the role.
 *
 * The all-caps contract lives entirely in this primitive: when
 * `transform === "uppercase"` (either explicit or via a caps role), the
 * tracking automatically resolves through `typography.caps.*` so callers
 * never have to remember to letterspace their UPPERCASE text.
 *
 * Inline `style` is a controlled escape hatch — merged after the computed
 * style so callers can pin one-off polish (margin, gradient text, etc.)
 * without forking the primitive.
 */

export type TextAs =
    | "p"
    | "span"
    | "h1"
    | "h2"
    | "h3"
    | "h4"
    | "h5"
    | "h6"
    | "label"
    | "small"
    | "div";
export type TextTone = "default" | "muted" | "subtle" | "accent" | "success" | "warning" | "danger";
export type TextLeading = TypographyLineHeightKey;
export type TextTransform = "none" | "uppercase";
/**
 * Tracking accepts either an `optical` key (size-correlated, used for
 * non-caps text), a `caps` key prefixed with `caps-` (used for uppercase
 * text), or `auto` (resolves to the role default, or to `optical[size]`
 * when no role is set).
 */
export type TextTracking = "auto" | TypographyOpticalKey | `caps-${TypographyCapsKey}`;

export interface TextProps {
    as?: TextAs;
    /**
     * Semantic role from the type ramp. When set, defaults all four
     * perceptual properties (size/weight/leading/tracking) plus
     * transform and family. Override any individual property by setting
     * it explicitly.
     */
    role?: TypographyRoleKey;
    size?: TypographyScaleKey;
    weight?: TypographyWeightKey;
    tone?: TextTone;
    leading?: TextLeading;
    /**
     * Override the role's tracking, or supply a tracking when no role
     * is set. `auto` (default) follows the role, falling back to
     * `typography.optical[size]` for a non-role text.
     */
    tracking?: TextTracking;
    /**
     * Case transformation. Defaults to the role's transform; defaults
     * to `none` when no role is set. Setting `uppercase` automatically
     * widens the tracking to the caps scale unless `tracking` is also
     * explicitly set.
     */
    transform?: TextTransform;
    /**
     * Limit text to a single line with ellipsis truncation. Sets a block
     * box (`display: block`, `maxWidth: 100%`) plus `whiteSpace: nowrap`,
     * `overflow: hidden`, `textOverflow: ellipsis`. Inline spans cannot
     * ellipsize and paint through neighbouring list rows.
     */
    truncate?: boolean;
    /**
     * Tabular numerals — keep digits at fixed widths so timestamps and
     * counts don't shift width as values change. Atlas 02 §9 ("data-ink").
     */
    tabular?: boolean;
    /**
     * Inline style escape hatch. Merged after computed style.
     */
    style?: CSSProperties;
    /**
     * Optional className for callers using utility CSS.
     */
    className?: string;
    /**
     * Pass-through HTML id for label-association and aria-labelledby
     * targeting. Common enough to be a first-class prop rather than an
     * arbitrary DOM-attribute escape hatch.
     */
    id?: string;
    /**
     * `for` attribute when `as="label"`. Required for any visible form
     * label so screen readers and click-to-focus behavior wire correctly.
     */
    htmlFor?: string;
    children: ReactNode;
}

const toneColor: Record<TextTone, string> = {
    default: color.fg.default,
    muted: color.fg.muted,
    subtle: color.fg.subtle,
    accent: color.accent.primary,
    success: color.accent.success,
    warning: color.accent.warning,
    danger: color.accent.danger,
};

// Sensible default leading per size — display/heading sizes get tight,
// body sizes get normal, micro caption stays snug for compact rows.
const defaultLeading: Record<TypographyScaleKey, TextLeading> = {
    xs: "snug",
    sm: "snug",
    base: "normal",
    md: "normal",
    lg: "snug",
    xl: "snug",
    xxl: "tight",
    xxxl: "tight",
};

/**
 * Resolve the four perceptual properties into a concrete bundle. Role
 * defaults are applied first, then any explicit props override.
 */
interface ResolvedTypeBundle {
    fontFamily: string;
    fontSize: string;
    fontWeight: number;
    lineHeight: number;
    letterSpacing: string;
    textTransform: "none" | "uppercase";
    fontVariantNumeric: string | undefined;
}

function resolveBundle(props: TextProps): ResolvedTypeBundle {
    const role = props.role !== undefined ? typographyRoles[props.role] : undefined;

    const family = typography.family[role?.family ?? "system"];

    const size: TypographyScaleKey = props.size ?? role?.size ?? "md";
    const weight: TypographyWeightKey = props.weight ?? role?.weight ?? "regular";
    const leading: TextLeading = props.leading ?? role?.leading ?? defaultLeading[size];
    const transform: TextTransform = props.transform ?? role?.transform ?? "none";

    let letterSpacing: string;
    const trackingProp = props.tracking ?? "auto";
    if (trackingProp === "auto") {
        if (role !== undefined) {
            // Honor the role's tracking *unless* the caller has overridden
            // transform (e.g. role=body, transform=uppercase) — then we
            // promote tracking to caps so the override stays coherent.
            if (transform === "uppercase" && role.transform !== "uppercase") {
                letterSpacing = typography.caps.medium;
            } else if (role.tracking.kind === "caps") {
                letterSpacing = typography.caps[role.tracking.key];
            } else {
                letterSpacing = typography.optical[role.tracking.key];
            }
        } else {
            // No role. Pick by transform: caps text always tracks loose;
            // body text follows the optical curve for its size.
            letterSpacing =
                transform === "uppercase" ? typography.caps.medium : typography.optical[size];
        }
    } else if (trackingProp.startsWith("caps-")) {
        const key = trackingProp.slice("caps-".length) as TypographyCapsKey;
        letterSpacing = typography.caps[key];
    } else {
        letterSpacing = typography.optical[trackingProp as TypographyOpticalKey];
    }

    const tabular = props.tabular ?? role?.tabular ?? false;

    return {
        fontFamily: family,
        fontSize: typography.scale[size],
        fontWeight: typography.weights[weight],
        lineHeight: typography.lineHeights[leading],
        letterSpacing,
        textTransform: transform,
        fontVariantNumeric: tabular ? "tabular-nums" : undefined,
    };
}

export function Text(props: TextProps): ReactElement {
    const as = props.as ?? "span";
    const tone = props.tone ?? "default";
    const truncate = props.truncate ?? false;

    const bundle = resolveBundle(props);

    const computed: CSSProperties = {
        margin: 0,
        fontFamily: bundle.fontFamily,
        fontSize: bundle.fontSize,
        fontWeight: bundle.fontWeight,
        color: toneColor[tone],
        lineHeight: bundle.lineHeight,
        letterSpacing: bundle.letterSpacing,
        textTransform: bundle.textTransform,
        ...(bundle.fontVariantNumeric !== undefined
            ? { fontVariantNumeric: bundle.fontVariantNumeric }
            : {}),
        ...(truncate
            ? {
                  // Ellipsis only applies to a block box with a bounded
                  // width. An inline span paints through the parent and
                  // through the next list row.
                  display: "block",
                  maxWidth: "100%",
                  whiteSpace: "nowrap" as const,
                  overflow: "hidden" as const,
                  textOverflow: "ellipsis" as const,
                  // `min-width: 0` is required for truncate to work inside
                  // a flex container — without it the text overflows the
                  // parent rather than ellipsizing.
                  minWidth: 0,
              }
            : {}),
    };

    const style: CSSProperties =
        props.style !== undefined ? { ...computed, ...props.style } : computed;

    return createElement(
        as,
        {
            style,
            className: props.className,
            ...(props.id !== undefined ? { id: props.id } : {}),
            ...(props.htmlFor !== undefined ? { htmlFor: props.htmlFor } : {}),
            "data-tone": tone,
            ...(props.role !== undefined ? { "data-role": props.role } : {}),
        },
        props.children,
    );
}
